// Pricing & promotions' server state (spec/13 §18; F-POS-02, F-POS-03, F-POS-06), as TanStack Query
// hooks over the generated client — `packages/contracts/src/routes/{promotions,discounts,coupons}.ts`:
//
//   GET   /v1/organizations/{o}/promotions              the organisation's promotions   discounts:manage
//   POST  /v1/organizations/{o}/promotions              define one                      discounts:manage
//   PATCH /v1/organizations/{o}/promotions/{id}         edit, pause or resume one       discounts:manage
//   GET   /v1/organizations/{o}/discount-codes          the organisation's codes        discounts:read
//   POST  /v1/organizations/{o}/discount-codes          define one                      discounts:manage
//   GET   /v1/organizations/{o}/coupon-batches          the batches, with counts        discounts:manage
//   POST  /v1/organizations/{o}/coupon-batches          make a batch and its codes      discounts:manage
//
// All three are organisation-scoped: a promotion, code or batch is defined once and names the venues
// it applies at. Money is integer minor units on the wire; the screens show it in rupees. A batch's
// codes come back once, in the create response — the list never repeats them — so the dialog that
// made a batch is the one place to copy or download them.

import type {
  CouponBatch,
  CreateCouponBatchRequest,
  CreateDiscountCodeRequest,
  CreatedCouponBatch,
  CreatePromotionRequest,
  DiscountCode,
  Promotion,
  PublishRateCard,
  RateCardView,
  UpdatePromotionRequest,
} from '@clavian/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../../api/client';

export const pricingKeys = {
  promotions: (organizationId: string) => ['pricing', organizationId, 'promotions'] as const,
  codes: (organizationId: string) => ['pricing', organizationId, 'discount-codes'] as const,
  batches: (organizationId: string) => ['pricing', organizationId, 'coupon-batches'] as const,
  rateCard: (organizationId: string, venueId: string) => ['pricing', organizationId, venueId, 'rate-card'] as const,
};

/**
 * A venue's rate card (F-PRC-10 … F-PRC-12, spec/06 §4): the version in force, the scheduled ones, the history, what
 * each class pays now, and the flat rate a class the card leaves out keeps. Needs `pricing:read`.
 */
export function useRateCard(organizationId: string, venueId: string, enabled = true) {
  const api = useApi();
  return useQuery<RateCardView>({
    queryKey: pricingKeys.rateCard(organizationId, venueId),
    queryFn: () => api.getVenueRateCard({ path: { organizationId, venueId } }),
    enabled,
    retry: false,
  });
}

/** Publish a new version of the venue's rate card (audited). Needs `pricing:manage`. */
export function usePublishRateCard(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PublishRateCard) => api.publishVenueRateCard({ path: { organizationId, venueId }, body }),
    onSuccess: (view) => queryClient.setQueryData(pricingKeys.rateCard(organizationId, venueId), view),
  });
}

/** The organisation's automatic promotions (F-POS-03). Needs `discounts:manage`. */
export function usePromotions(organizationId: string, enabled = true) {
  const api = useApi();
  return useQuery<readonly Promotion[]>({
    queryKey: pricingKeys.promotions(organizationId),
    queryFn: async () => (await api.listPromotions({ path: { organizationId } })).promotions,
    enabled,
    retry: false,
  });
}

export function useCreatePromotion(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePromotionRequest) => api.createPromotion({ path: { organizationId }, body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: pricingKeys.promotions(organizationId) }),
  });
}

export function useUpdatePromotion(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ promotionId, body }: { readonly promotionId: string; readonly body: UpdatePromotionRequest }) =>
      api.updatePromotion({ path: { organizationId, promotionId }, body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: pricingKeys.promotions(organizationId) }),
  });
}

/** The organisation's discount codes (F-POS-02). Needs `discounts:read`. */
export function useDiscountCodes(organizationId: string, enabled = true) {
  const api = useApi();
  return useQuery<readonly DiscountCode[]>({
    queryKey: pricingKeys.codes(organizationId),
    queryFn: async () => (await api.listDiscountCodes({ path: { organizationId } })).codes,
    enabled,
    retry: false,
  });
}

export function useCreateDiscountCode(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDiscountCodeRequest) => api.createDiscountCode({ path: { organizationId }, body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: pricingKeys.codes(organizationId) }),
  });
}

/** Switch a discount code off or back on (F-POS-02). Needs `discounts:manage`. */
export function useSetDiscountCodeActive(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ discountCodeId, active }: { readonly discountCodeId: string; readonly active: boolean }) =>
      api.updateDiscountCode({ path: { organizationId, discountCodeId }, body: { active } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: pricingKeys.codes(organizationId) }),
  });
}

/** The organisation's coupon batches with their issued and redeemed counts (F-POS-06). Needs `discounts:manage`. */
export function useCouponBatches(organizationId: string, enabled = true) {
  const api = useApi();
  return useQuery<readonly CouponBatch[]>({
    queryKey: pricingKeys.batches(organizationId),
    queryFn: async () => (await api.listCouponBatches({ path: { organizationId } })).batches,
    enabled,
    retry: false,
  });
}

export function useCreateCouponBatch(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<CreatedCouponBatch, unknown, CreateCouponBatchRequest>({
    mutationFn: (body) => api.createCouponBatch({ path: { organizationId }, body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: pricingKeys.batches(organizationId) }),
  });
}
